import { useMemo, useState } from "react";
import { topicWall } from "@/mocks/home";
import Reveal from "@/components/base/Reveal";

const filters = [
  { key: "all", label: "全部" },
  { key: "hot", label: "热议" },
  { key: "fresh", label: "最新" },
] as const;

type FilterKey = (typeof filters)[number]["key"];

export default function TopicWall() {
  const [active, setActive] = useState<FilterKey>("all");

  const tags = useMemo(() => {
    if (active === "hot") return topicWall.filter((tag) => tag.level !== "normal");
    if (active === "fresh") return topicWall.slice(4, 12);
    return topicWall;
  }, [active]);

  return (
    <section id="topic-wall" className="scroll-mt-24">
      <div className="mx-auto w-full max-w-[1240px] px-4 py-12 md:px-6 md:py-16">
        <Reveal className="flex flex-col gap-4 border-b border-background-300/70 pb-5 md:flex-row md:items-end md:justify-between">
          <div>
            <p className="text-xs tracking-widest text-primary-600">TOPIC WALL</p>
            <h2 className="font-heading mt-2 text-2xl font-bold text-foreground-950 md:text-3xl">
              标签墙 · 正在被围观的话题
            </h2>
            <p className="mt-2 max-w-xl text-sm leading-relaxed text-foreground-600">
              话题热度由近期被打开的次数决定。字越大、色越重，说明最近被翻得越多。
            </p>
          </div>
          <div className="flex w-fit items-center gap-1 rounded-full border border-background-300 bg-background-100 p-1">
            {filters.map((filter) => {
              const isActive = filter.key === active;
              return (
                <button
                  key={filter.key}
                  type="button"
                  onClick={() => setActive(filter.key)}
                  className={`rounded-full px-3.5 py-1.5 text-sm transition-colors whitespace-nowrap ${
                    isActive
                      ? "bg-foreground-950 text-background-50"
                      : "text-foreground-600 hover:text-foreground-950"
                  }`}
                >
                  {filter.label}
                </button>
              );
            })}
          </div>
        </Reveal>

        <Reveal delay={80}>
          <div className="mt-7 flex flex-wrap gap-2.5 md:gap-3">
            {tags.map((tag) => {
              const isHot = tag.level === "hot";
              const isWarm = tag.level === "warm";
              const sizeClass = isHot ? "text-base md:text-lg" : "text-sm md:text-[15px]";
              const colorClass = isHot
                ? "border-primary-200 bg-primary-100 text-primary-700 hover:border-primary-400"
                : isWarm
                  ? "border-accent-200 bg-accent-100 text-accent-900 hover:border-accent-400"
                  : "border-background-300 bg-background-50 text-foreground-700 hover:border-foreground-300";
              return (
                <a
                  key={tag.name}
                  href="#exclusive"
                  className={`flex items-center gap-2 rounded-full border px-3.5 py-2 transition-colors md:px-4 ${colorClass}`}
                >
                  <span className={`font-heading font-medium ${sizeClass}`}>{tag.name}</span>
                  <span className="rounded-full bg-background-50/70 px-1.5 text-xs tabular-nums text-foreground-500">
                    {tag.count}
                  </span>
                </a>
              );
            })}
          </div>
        </Reveal>

        <Reveal delay={140}>
          <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-2 rounded-lg border border-background-300/70 bg-background-100/70 px-4 py-3 text-xs text-foreground-600 md:px-5">
            <span className="flex items-center gap-1.5">
              <span className="h-2.5 w-2.5 rounded-full bg-primary-300" aria-hidden="true" />
              大字 = 热议话题
            </span>
            <span className="flex items-center gap-1.5">
              <span className="h-2.5 w-2.5 rounded-full bg-accent-300" aria-hidden="true" />
              中等 = 稳定增长
            </span>
            <span className="flex items-center gap-1.5">
              <span className="h-2.5 w-2.5 rounded-full bg-background-400" aria-hidden="true" />
              小字 = 新近收录
            </span>
          </div>
        </Reveal>
      </div>
    </section>
  );
}